<script setup>
import { ref, computed, getCurrentInstance } from "vue";
import api from "../api/api.js";
import { flashMessage } from "../functions.js";

const profile = ref({
  guestProfileId: "GP-00071042",
  loyaltyNumber: "OD-221983",
  loyaltyProgram: "OUTRIGGER DISCOVERY",
  loyaltyTier: "Silver",
  discoveryDollars: 48.00,
  totalStays: 1,
  nightsThisYear: 5,
  firstName: "John",
  lastName: "Smith",
  dateOfBirth: "1988-03-14",
  nationality: "American",
  idType: "Driver's License",
  idNumber: "IL-8834921",
  email: "john.smith@email.com",
  phone: "+1 (312) 752-8183",
  address: "412 N. Michigan Ave, Chicago, Illinois, USA 60611",
  preferences: "High floor · Non-smoking · King bed",
  createdDate: "2026-10-01",
  lastUpdated: "2026-10-01",
});

const guest = ref({
  reservationId: "864221",
  status: "Confirmed",
  property: "Outrigger Waikiki Paradise Hotel",
  location: "Waikiki, Honolulu, Hawaii, USA",
  room: "Deluxe Suite",
  checkIn: "2026-10-10",
  checkOut: "2026-10-15",
  nights: 5,
  ratePerNight: 380,
  guests: 2,
  firstName: "John",
  lastName: "Smith",
  email: "john.smith@email.com",
  phone: "+1 (312) 752-8183",
  address: "412 N. Michigan Ave, Chicago, Illinois, USA 60611",
  paymentStatus: "Paid",
  totalCharges: 1900,
  amountPaid: 1900,
  outstandingBalance: 0,
  pendingItems: [
    { item: "Minibar Consumption (pre-arrival stock)", amount: 0 },
    { item: "Resort Fee (per night × 5)",              amount: 175 },
  ],
  specialRequests: [],
});

const remarks = ref([
  { id: 1, date: "2026-10-01", type: "Note",    agent: "K. Akana",  note: "Guest called to confirm reservation 864221. Verified identity by last name and email. Noted first-time stay at Outrigger." },
  { id: 2, date: "2026-10-01", type: "Request", agent: "L. Kahale", note: "Guest requested high-floor room with ocean view if available. Added to reservation notes." },
]);

const upsells = ref([
  { id: 1, icon: "💆", title: "Spa Treatment",           description: "60-min massage or facial at Waikiki Spa",       price: "$85",  offered: false },
  { id: 2, icon: "🍽️", title: "Dinner at Duke's Waikiki", description: "Oceanfront table for 2 at Duke's Waikiki",      price: "$148", offered: false },
  { id: 3, icon: "🤿", title: "Hanauma Bay Snorkel",      description: "Guided reef snorkel tour with equipment",        price: "$65",  offered: false },
  { id: 4, icon: "🚁", title: "Island Helicopter Tour",   description: "20-min doors-off helicopter tour over O'ahu",    price: "$220", offered: false },
]);

const showIdNumber  = ref(false);
const newRemarkNote = ref("");
const newRemarkType = ref("Request");
const remarkTypes   = ["Request", "Complaint", "Upgrade", "Discount", "Note"];

const fmt = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2 });

const balancePct = computed(() =>
  guest.value.totalCharges === 0 ? 100 : Math.round((guest.value.amountPaid / guest.value.totalCharges) * 100)
);

const remarkTypeColor = (type) => ({
  Request:   "#1d6fa5",
  Complaint: "#b91c1c",
  Upgrade:   "#065f46",
  Discount:  "#92400e",
  Note:      "#374151",
}[type] || "#374151");

function saveRemark() {
  if (!newRemarkNote.value.trim()) return;
  remarks.value.unshift({
    id: Date.now(),
    date: new Date().toISOString().slice(0, 10),
    type: newRemarkType.value,
    agent: "Current Agent",
    note: newRemarkNote.value.trim(),
  });
  newRemarkNote.value = "";
}

// ── OTP ──────────────────────────────────────────────────────────────────────
const OTP_PHONE = "+13127528183";
const otpInput              = ref("");
const verificationAttempted = ref(false);
const isValid               = ref(false);
const logocheck             = require("@/assets/check.png");
const logoclose             = require("@/assets/close.png");
const { proxy }             = getCurrentInstance();

function flash(message, color = "#4CAF50") {
  flashMessage(proxy.$flashMessage, color, "#ffffff", message);
}

function openPMS() {
  window.open("https://mnet.core-access.com/outrigger/pms/", "_blank", "width=1280,height=800");
}

async function sendSMS() {
  try {
    const response = await api.post("/v2/SendOTP", {
      Company: "5028",
      cellPhone: OTP_PHONE,
      AuthToken: '3301',
      memberNumber: '774513',
    }, {
      headers: { token: "2y10D08eZgJLx0C9rGvQKfwFBuCPvqDdmdowpnczUKwYWU3B0VoVt5BiC" }
    });
    flash(response.data.Response || "OTP sent");
  } catch (e) {
    flash("Failed to send OTP", "#FF0000");
  }
}

async function verifyOTP() {
  try {
    const response = await api.post("/v2/CheckOTP", {
      Company: "5028",
      cellPhone: OTP_PHONE,
      OTP: otpInput.value,
      AuthToken: '3301',
    }, {
      headers: { token: "2y10D08eZgJLx0C9rGvQKfwFBuCPvqDdmdowpnczUKwYWU3B0VoVt5BiC" }
    });
    verificationAttempted.value = true;
    isValid.value = response.data.Response === "OTP Validated";
    flash(response.data.Response, isValid.value ? "#4CAF50" : "#FF0000");
  } catch (e) {
    flash("Failed to verify OTP", "#FF0000");
  }
}
</script>

<template>
  <div class="gi-page">
    <div class="gi-card">

      <!-- ── Topbar ─────────────────────────────────────────────────────── -->
      <div class="gi-topbar">
        <h2 class="gi-screen-id">Guest Information</h2>
        <div class="gi-topbar-right">
          <button class="gi-goto-btn" @click="openPMS">Go to PMS</button>
          <span class="gi-screen-code">G01</span>
        </div>
      </div>

      <!-- ── Subheader ──────────────────────────────────────────────────── -->
      <div class="gi-subheader">
        <div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Reservation:</span><span class="gi-sub-val">{{ guest.reservationId }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Name:</span><span class="gi-sub-val gi-sub-val--name">{{ guest.firstName }} {{ guest.lastName }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Email:</span><span class="gi-sub-val">{{ guest.email }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Phone:</span><span class="gi-sub-val">{{ guest.phone }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Address:</span><span class="gi-sub-val">{{ guest.address }}</span></div>
        </div>
        <div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Status:</span><span class="gi-sub-val gi-sub-val--status">{{ guest.status }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Property:</span><span class="gi-sub-val">{{ guest.property }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Room:</span><span class="gi-sub-val">{{ guest.room }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Check-in:</span><span class="gi-sub-val">{{ guest.checkIn }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Check-out:</span><span class="gi-sub-val">{{ guest.checkOut }} &nbsp;({{ guest.nights }} nights · {{ guest.guests }} guests)</span></div>
        </div>
        <div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Payment:</span><span class="gi-sub-val" style="font-weight:700;">{{ guest.paymentStatus }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Total:</span><span class="gi-sub-val">{{ fmt(guest.totalCharges) }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Paid:</span><span class="gi-sub-val" style="color:#16a34a; font-weight:600;">{{ fmt(guest.amountPaid) }}</span></div>
          <div class="gi-sub-row"><span class="gi-sub-lbl">Outstanding:</span><span class="gi-sub-val" style="color:#dc2626; font-weight:700;">{{ fmt(guest.outstandingBalance) }}</span></div>
          <div class="gi-progress-wrap" style="margin-top:6px;">
            <div class="gi-progress-bar">
              <div class="gi-progress-fill" :style="{ width: balancePct + '%' }"></div>
            </div>
            <span class="gi-progress-lbl">{{ balancePct }}% paid</span>
          </div>
        </div>
      </div>

      <!-- ── 3-column body ──────────────────────────────────────────────── -->
      <div class="gi-centerdiv">

        <!-- COL 1: Identity + OTP + Special Requests ──────────────────── -->
        <div class="gi-col">

          <h4>Search for</h4>
          <div class="gi-search-group">
            <input class="gi-search-input" type="text" placeholder="Itinerary, confirmation, or item number" />
            <input class="gi-search-input" type="text" placeholder="Email Address" />
          </div>
          <div style="margin: 8px 0 18px;">
            <button class="gi-submit-btn">Find</button>
            <button class="gi-cancel-btn">Cancel</button>
          </div>

          <h4>Guest Identity</h4>

          <div class="gi-profile-ids">
            <div class="gi-profile-id-item">
              <span class="gi-sub-lbl">Profile ID</span>
              <span class="gi-profile-id-val">{{ profile.guestProfileId }}</span>
            </div>
            <div class="gi-profile-id-item">
              <span class="gi-sub-lbl">Loyalty No.</span>
              <span class="gi-profile-id-val">{{ profile.loyaltyNumber }}</span>
            </div>
            <div class="gi-profile-id-item">
              <span class="gi-sub-lbl">Tier</span>
              <span class="gi-loyalty-badge" :class="'tier-' + profile.loyaltyTier.toLowerCase()">
                {{ profile.loyaltyTier }}
              </span>
            </div>
            <div class="gi-profile-id-item">
              <span class="gi-sub-lbl">Total Stays</span>
              <span class="gi-profile-id-val">{{ profile.totalStays }}</span>
            </div>
            <div class="gi-profile-id-item">
              <span class="gi-sub-lbl">Nights (YTD)</span>
              <span class="gi-profile-id-val">{{ profile.nightsThisYear }} / 10
                <span class="gi-nights-hint">→ Platinum</span>
              </span>
            </div>
          </div>

          <table class="gi-info-table" style="margin-top:8px;">
            <tbody>
              <tr><td class="gi-td-lbl">ID Type</td><td>{{ profile.idType }}</td></tr>
              <tr>
                <td class="gi-td-lbl">ID Number</td>
                <td>
                  {{ showIdNumber ? profile.idNumber : "••••••••••" }}
                  <button class="gi-pill-btn" @click="showIdNumber = !showIdNumber">
                    {{ showIdNumber ? "Hide" : "Show" }}
                  </button>
                </td>
              </tr>
              <tr><td class="gi-td-lbl">Nationality</td><td>{{ profile.nationality }}</td></tr>
              <tr><td class="gi-td-lbl">Date of Birth</td><td>{{ profile.dateOfBirth }}</td></tr>
              <tr><td class="gi-td-lbl">Preferences</td><td>{{ profile.preferences }}</td></tr>
              <tr><td class="gi-td-lbl">Profile Since</td><td>{{ profile.createdDate }}</td></tr>
              <tr><td class="gi-td-lbl">Last Updated</td><td>{{ profile.lastUpdated }}</td></tr>
            </tbody>
          </table>

          <!-- 2FA / OTP -->
          <fieldset style="margin-top:20px; border:1px solid #e5e7eb; border-radius:6px; padding:8px 12px; display:inline-flex; flex-direction:column; gap:8px;">
            <legend style="font-weight:600; font-size:13px; color:#374151; padding:0 6px;">2FA</legend>
            <div style="display:flex; align-items:center; gap:6px;">
              <input class="gi-search-input" :value="guest.phone" disabled style="background:#f3f4f6; color:#374151; margin:0; flex:1;" />
              <button class="gi-submit-btn" @click="sendSMS" style="margin:0; white-space:nowrap;">Send SMS</button>
            </div>
            <div style="display:flex; align-items:center; gap:6px;">
              <input class="gi-search-input" v-model="otpInput" placeholder="OTP" style="margin:0; width:80px; flex:none;" />
              <img v-if="verificationAttempted && isValid"  :src="logocheck" alt="valid"   style="width:18px;height:18px;flex-shrink:0;" />
              <img v-if="verificationAttempted && !isValid" :src="logoclose" alt="invalid" style="width:18px;height:18px;flex-shrink:0;" />
              <button class="gi-submit-btn" @click="verifyOTP" style="margin:0;">Validate</button>
            </div>
          </fieldset>

          <h4 style="margin-top:20px;">Special Requests</h4>
          <textarea class="gi-textarea" rows="3" readonly
            :value="guest.specialRequests.join('\n')"></textarea>
        </div>

        <!-- COL 2: Remarks ────────────────────────────────────────────── -->
        <div class="gi-col">
          <h4>Remarks &amp; Guest History</h4>
          <div style="display:flex; gap:8px; margin-bottom:10px; align-items:flex-start;">
            <div class="gi-remark-type-wrap">
              <select class="gi-remark-type-select" v-model="newRemarkType">
                <option v-for="t in remarkTypes" :key="t">{{ t }}</option>
              </select>
              <span class="gi-remark-type-chevron">▾</span>
            </div>
            <textarea class="gi-textarea" v-model="newRemarkNote"
              placeholder="Add a remark..." rows="2"
              style="flex:1; margin:0;"></textarea>
          </div>
          <div style="margin-bottom:14px;">
            <button class="gi-submit-btn" @click="saveRemark">Save</button>
            <button class="gi-cancel-btn" @click="newRemarkNote=''">Cancel</button>
          </div>

          <table class="gi-data-table">
            <thead>
              <tr><th>Type</th><th>Date</th><th>Agent</th><th>Note</th></tr>
            </thead>
            <tbody>
              <tr v-for="r in remarks" :key="r.id">
                <td style="white-space:nowrap;">
                  <span class="gi-type-dot" :style="{ background: remarkTypeColor(r.type) }"></span>
                  {{ r.type }}
                </td>
                <td style="white-space:nowrap; color:#555;">{{ r.date }}</td>
                <td style="white-space:nowrap; color:#555;">{{ r.agent }}</td>
                <td>{{ r.note }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- COL 3: Upsells + Pending Items ────────────────────────────── -->
        <div class="gi-col">
          <h4>Personalized Offers</h4>
          <p class="gi-hint">Click a row to mark as offered to the guest.</p>
          <table class="gi-data-table">
            <thead>
              <tr><th></th><th>Service</th><th>Price</th><th>Status</th></tr>
            </thead>
            <tbody>
              <tr v-for="u in upsells" :key="u.id"
                class="gi-upsell-row" :class="{ offered: u.offered }"
                @click="u.offered = !u.offered">
                <td>{{ u.icon }}</td>
                <td>{{ u.title }}</td>
                <td style="white-space:nowrap;">{{ u.price }}</td>
                <td>
                  <span v-if="u.offered" class="gi-offered-badge">Offered ✓</span>
                  <span v-else class="gi-pending-badge">—</span>
                </td>
              </tr>
            </tbody>
          </table>

          <h4 style="margin-top:20px;">Pending Items to Settle</h4>
          <table class="gi-data-table">
            <thead>
              <tr><th>Item</th><th>Amount</th></tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in guest.pendingItems" :key="i">
                <td>{{ item.item }}</td>
                <td style="text-align:right; white-space:nowrap;">{{ fmt(item.amount) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td><strong>Total Outstanding</strong></td>
                <td style="text-align:right; white-space:nowrap;">
                  <strong>{{ fmt(guest.pendingItems.reduce((s, i) => s + i.amount, 0)) }}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.gi-page { font-family: "Open Sans", "Segoe UI", sans-serif; font-size: 15px; background: #e8e8e8; min-height: 100vh; padding: 20px; color: #222; box-sizing: border-box; }
.gi-card { background: #fff; border-radius: 10px; box-shadow: 0 1px 6px rgba(0,0,0,0.12); overflow: hidden; }
.gi-topbar { padding: 14px 20px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #e8e8e8; }
.gi-screen-id { font-size: 18px; font-weight: 600; color: #222; margin: 0; }
.gi-screen-code { font-size: 18px; font-weight: 600; color: #888; }
.gi-topbar-right { display: flex; align-items: center; gap: 14px; }
.gi-goto-btn { background: #0e5071; color: #fff; border: none; border-radius: 50px; padding: 6px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.gi-goto-btn:hover { background: #0c4160; }

.gi-subheader { border-bottom: 1px solid #e8e8e8; padding: 10px 20px; display: flex; gap: 60px; }
.gi-sub-row  { display: flex; gap: 6px; padding: 1px 0; font-size: 14px; }
.gi-sub-lbl  { color: #555; min-width: 80px; }
.gi-sub-val  { color: #222; }
.gi-sub-val--name   { font-weight: 600; }
.gi-sub-val--status { font-weight: 700; color: #1d6fa5; }

.gi-centerdiv { display: flex; gap: 0; padding: 0; align-items: flex-start; }
.gi-col { flex: 1; padding: 16px 20px; min-width: 0; border-right: 1px solid #e8e8e8; }
.gi-col:last-child { border-right: none; }
.gi-col h4 { font-size: 14px; font-weight: 600; margin: 0 0 10px; color: #222; }

.gi-profile-ids { display: flex; gap: 0; background: #f5f7f9; border: 1px solid #e0e0e0; border-radius: 4px; overflow: hidden; }
.gi-profile-id-item { flex: 1; padding: 7px 10px; border-right: 1px solid #e0e0e0; display: flex; flex-direction: column; gap: 2px; }
.gi-profile-id-item:last-child { border-right: none; }
.gi-profile-id-val { font-size: 13px; font-weight: 600; color: #0e5071; font-family: monospace; }
.gi-loyalty-badge { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; margin-top: 1px; }
.tier-gold     { background: #fef3c7; color: #92400e; }
.tier-silver   { background: #f1f5f9; color: #475569; }
.tier-platinum { background: #ede9fe; color: #5b21b6; }
.tier-titanium { background: #1e293b; color: #e2e8f0; }
.gi-nights-hint { font-size: 10px; color: #888; font-family: sans-serif; margin-left: 2px; }

.gi-search-group { display: flex; flex-direction: column; gap: 6px; }
.gi-search-input { width: 100%; border: 1px solid #ccc; border-radius: 3px; padding: 5px 8px; font-size: 14px; font-family: inherit; box-sizing: border-box; }
.gi-search-input:focus { outline: none; border-color: #0e5071; }

.gi-info-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.gi-info-table td { padding: 4px 6px; vertical-align: top; }
.gi-td-lbl { color: #555; width: 110px; white-space: nowrap; }

.gi-data-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.gi-data-table thead tr { background: #0e5071; }
.gi-data-table th { padding: 7px 10px; text-align: left; font-weight: 600; font-size: 13px; color: #fff !important; white-space: nowrap; }
.gi-data-table tbody tr:nth-child(odd)  { background: #fff; }
.gi-data-table tbody tr:nth-child(even) { background: #f5f7f9; }
.gi-data-table tbody tr { border-bottom: 1px solid #e8e8e8; }
.gi-data-table tbody tr:last-child { border-bottom: none; }
.gi-data-table td { padding: 6px 10px; vertical-align: top; color: #333; }
.gi-data-table tfoot td { background: #fff; border-top: 2px solid #ddd; padding-top: 8px; }

.gi-progress-wrap { display: flex; align-items: center; gap: 8px; margin: 8px 0 0; }
.gi-progress-bar  { flex: 1; height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; }
.gi-progress-fill { height: 100%; background: #16a34a; border-radius: 4px; }
.gi-progress-lbl  { font-size: 12px; color: #555; white-space: nowrap; }

.gi-textarea { width: 100%; border: 1px solid #ccc; border-radius: 3px; padding: 6px 8px; font-size: 14px; font-family: inherit; resize: vertical; color: #222; box-sizing: border-box; }

.gi-remark-type-wrap { position: relative; display: inline-flex; align-items: center; }
.gi-remark-type-select { appearance: none; -webkit-appearance: none; border: 1px solid #b0b0b0; border-radius: 4px; padding: 5px 28px 5px 10px; font-size: 13px; font-family: inherit; font-weight: 600; background: #fff; color: #454a4d; cursor: pointer; min-width: 100px; }
.gi-remark-type-select:focus { outline: none; border-color: #0e5071; }
.gi-remark-type-chevron { position: absolute; right: 8px; pointer-events: none; font-size: 11px; color: #888; }

.gi-submit-btn, .gi-cancel-btn, .gi-pill-btn { border: none; border-radius: 20px; padding: 5px 18px; font-size: 13px; cursor: pointer; margin-right: 6px; }
.gi-submit-btn { background: #0e5071; color: #fff; }
.gi-submit-btn:hover { background: #0c4160; }
.gi-cancel-btn { background: #b0b0b0; color: #fff; }
.gi-cancel-btn:hover { background: #999; }
.gi-pill-btn   { background: #e0e0e0; color: #333; font-size: 11px; padding: 2px 10px; margin-left: 6px; }

.gi-type-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 4px; vertical-align: middle; }
.gi-hint { font-size: 12px; color: #888; margin: -6px 0 8px; }
.gi-upsell-row { cursor: pointer; }
.gi-upsell-row:hover { background: #f5f9fc; }
.gi-upsell-row.offered { background: #f0fdf4; }
.gi-offered-badge { background: #d1fae5; color: #065f46; padding: 1px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.gi-pending-badge { color: #aaa; font-size: 13px; }
</style>
